<script lang="ts" setup>
  import type { Reason } from '@/types'
  import axios from 'axios'
  import AppConfirm from '@/components/AppConfirm.vue'

  const emit = defineEmits<{
    back: []
  }>()

  const subscriptionStore = useSubscriptionsStore()
  const reasonStore = useReasonStore()
  const { reasons } = storeToRefs(reasonStore)
  const { t } = useI18n()

  const currentStep = ref(1)
  const selectedReason = ref<Reason | null>(null)
  const feedbackText = ref('')
  const isLoading = ref(false)
  const appConfirmRef = useTemplateRef<InstanceType<typeof AppConfirm>>('app-confirm')
  const errors = reactive<{
    reason_id?: string | string[]
    reason_text?: string | string[]
  }>({
    reason_id: undefined,
    reason_text: undefined,
  })

  const handleContinue = () => {
    if (currentStep.value === 1 && selectedReason.value && selectedReason.value.is_free_text) {
      currentStep.value = 2

      return
    }

    handleSubmit()
  }

  const handleSubmit = async () => {
    isLoading.value = true
    try {
      if (!selectedReason.value) return

      const res = await appConfirmRef.value?.open(t('subscription.cancel.confirm_title'), t('subscription.cancel.confirm_message'), '', {
        cancelText: t('common.back'),
        confirmText: t('subscription.cancel.confirm'),
        confirmColor: 'crimson',
      })

      if (!res) return

      await subscriptionStore.cancelSubscription({
        reason_id: selectedReason.value.id,
        reason_text: feedbackText.value,
      })

      useTrackStore().track('unsubscription_success_page_presented', {
        reason_id: selectedReason.value.id,
        reason_text: feedbackText.value,
      })

      emit('back')
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.errors['reason_id']) {
          errors.reason_id = error.response?.data?.errors['reason_id']
        }

        if (error.response?.data?.errors['reason_text']) {
          errors.reason_text = error.response?.data?.errors['reason_text']
        }

        if (errors.reason_id && currentStep.value === 2) {
          currentStep.value = 1
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  const goBack = () => {
    if (currentStep.value > 1) {
      currentStep.value -= 1
    } else {
      emit('back')
    }
  }

  onBeforeRouteLeave(() => {
    if (subscriptionStore.subscription?.cancelled_at) {
      return true
    }

    if (currentStep.value !== 1) {
      currentStep.value = 1

      return false
    }

    return true
  })

  onMounted(async () => {
    reasons.value = await reasonStore.getCancellationReasons()
  })
</script>

<template>
  <div class="d-flex flex-column cancel-subscription h-100">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon="mdi-arrow-left"
        size="small"
        variant="text"
        @click="goBack"
      />
      <h2 class="text-h6 ms-2">{{ t('subscription.cancel.title') }}</h2>
    </div>

    <!-- Step 1: Reason Selection -->
    <div class="pa-md-7 pa-4 d-flex flex-column flex-1-1">
      <template v-if="currentStep === 1">
        <!-- Title -->
        <template v-if="reasonStore.loading">
          <v-skeleton-loader
            class="mb-4"
            type="heading"
            width="80%"
          />
        </template>
        <template v-else>
          <h2 class="text-h6 font-weight-bold mb-4 text-grey-100">
            {{ t('cancellation.reason_prompt') }}
          </h2>
        </template>

        <!-- Skeleton Loading State -->
        <template v-if="reasonStore.loading">
          <div class="d-flex flex-column ga-3">
            <v-skeleton-loader
              v-for="n in 6"
              :key="n"
              class="rounded-lg bg-background"
              height="64"
              type="list-item-two-line"
            />
          </div>
        </template>

        <!-- Loaded Content -->
        <template v-else>
          <div class="d-flex flex-column ga-3">
            <v-card
              v-for="reason in reasons"
              :key="reason.id"
              class="cursor-pointer transition-all bg-background"
              color="background"
              flat
              @click="selectedReason = reason"
            >
              <v-card-text class="pa-4">
                <div class="d-flex ga-3 align-center">
                  <div class="text-surface">
                    <app-radio
                      color="primary"
                      density="compact"
                      :error="!!errors?.reason_id"
                      :error-messages="!!errors?.reason_id"
                      hide-details="auto"
                      :model-value="selectedReason?.id"
                      :value="reason.id"
                    />
                  </div>
                  <span class="text-body-2 flex-grow-1">
                    {{ reason.name }}
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </template>
      </template>

      <template v-else>
        <h2 class="text-h6 font-weight-bold mb-4 text-grey-100">
          {{ t('cancellation.improvement_prompt') }}
        </h2>

        <v-textarea
          v-model="feedbackText"
          auto-grow
          class="mb-6 flex-0-0-0"
          :error="!!errors?.reason_text"
          :error-messages="errors?.reason_text"
          hide-details="auto"
          :placeholder="t('cancellation.feedback_placeholder')"
          rows="6"
          variant="outlined"
        />
      </template>
      <v-btn
        block
        class="mt-md-auto mt-5 font-weight-bold"
        color="primary"
        :disabled="!selectedReason"
        :loading="isLoading"
        max-height="48"
        size="large"
        @click="handleContinue"
      >
        {{ t('common.continue') }}
      </v-btn>
    </div>
    <app-confirm ref="app-confirm" />
  </div>
</template>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.transition-all {
  transition: all 0.2s ease-in-out;
}

.cancel-subscription :deep(.v-skeleton-loader) {
  overflow: hidden;
  display: flex;
  align-items: center;
  align-content: center;

  .v-skeleton-loader__bone {
    height: 50%;
  }
}
</style>
